import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Settings as SettingsIcon, Save, Shield, UserCog, FileText, Sparkles, Users, LogOut, CreditCard, Check, Mail, Copy, X, ScrollText, Palette, Wand2, Type, Plug, Globe } from "lucide-react";
import { Auth, Admin, Billing } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SignRulesPanel from "@/components/SignRulesPanel";
import AppearancePanel from "@/components/AppearancePanel";
import { Settings as SettingsApi, useSetting, useAppSettings } from "@/lib/appSettings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  CLINICAL_USE_SHORT, CLINICAL_NOTICE_MEDIUM, LEGAL_NOTICE_FULL,
} from "@/lib/legalContent";
import { BrandingPanel, ContentPanel, AiControlPanel } from "@/components/admin/CmsPanels";
import IntegrationsPanel from "@/components/admin/IntegrationsPanel";
import { LANGS, getLang, setLang, useT } from "@/lib/i18n";

export default function SettingsPage() {
  const { user, refresh, logout } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [users, setUsers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [plans, setPlans] = useState([]);
  const [checkoutBusy, setCheckoutBusy] = useState("");
  const [search, setSearch] = useSearchParams();
  const isAdmin = user?.role === "admin";
  const defaultTab = search.get("tab") || "clinic";
  const settingsTitle = useSetting("settings.header.title", "Definições");

  useEffect(() => {
    if (user) {
      setForm({
        clinical_profile: { ...(user.clinical_profile || {}) },
        report_prefs:      { ...(user.report_prefs || {}) },
        ai_prefs:          { ...(user.ai_prefs || {}) },
        assessment_prefs:  { ...(user.assessment_prefs || {}) },
      });
    }
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      Admin.listUsers().then(setUsers).catch(() => {});
      Admin.listInvites().then(setInvites).catch(() => {});
    }
  }, [isAdmin]);

  useEffect(() => {
    Billing.plans().then(d => setPlans(d.plans || [])).catch(() => {});
  }, []);

  // Após regresso do Stripe Checkout — polling do status até "paid"
  useEffect(() => {
    const outcome = search.get("stripe");
    const session_id = search.get("session_id");
    if (outcome === "cancel") {
      toast.message("Pagamento cancelado", { description: "Podes tentar novamente quando quiseres." });
      const clean = new URLSearchParams(search); clean.delete("stripe"); setSearch(clean, { replace: true });
      return;
    }
    if (outcome !== "success" || !session_id) return;
    let cancelled = false;
    (async () => {
      for (let i = 0; i < 8 && !cancelled; i++) {
        try {
          const s = await Billing.status(session_id);
          if (s.payment_status === "paid") {
            toast.success("Plano atualizado com sucesso 🎉", { description: `Bem-vindo ao plano ${s.plan}!` });
            await refresh();
            const clean = new URLSearchParams(search);
            clean.delete("stripe"); clean.delete("session_id");
            clean.set("tab", "billing");
            setSearch(clean, { replace: true });
            return;
          }
          if (s.status === "expired") {
            toast.error("Sessão expirada — tenta novamente.");
            return;
          }
        } catch { /* continua */ }
        await new Promise(r => setTimeout(r, 2000));
      }
      if (!cancelled) toast.message("Estamos a confirmar o teu pagamento…", { description: "Atualiza a página em alguns segundos." });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!form || !user) return <div className="text-slate-500">A carregar…</div>;

  const save = async () => {
    setBusy(true);
    try {
      await Auth.updateSettings(form);
      await refresh();
      toast.success("Definições guardadas");
    } catch { toast.error("Não foi possível guardar"); }
    finally { setBusy(false); }
  };

  const onLogoUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo máximo 2MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setForm({ ...form, clinical_profile: { ...form.clinical_profile, logo_url: ev.target.result } });
    reader.readAsDataURL(f);
    e.target.value = "";
  };

  const toggleTherapist = async (u) => {
    try {
      const updated = await Admin.toggleUser(u.user_id, !u.is_active);
      setUsers((prev) => prev.map(x => x.user_id === u.user_id ? updated : x));
      toast.success(`${updated.name} · ${updated.is_active ? "ativado" : "desativado"}`);
    } catch { toast.error("Não foi possível alterar"); }
  };

  const inviteLink = (token) => `${window.location.origin}/login?invite=${token}`;

  const sendInvite = async () => {
    const email = inviteEmail.trim().toLowerCase();
    if (!email || !email.includes("@")) { toast.error("Email inválido"); return; }
    setInviteBusy(true);
    try {
      const res = await Admin.createInvite(email);
      const link = res.invite_link || inviteLink(res.invite.token);
      try { await navigator.clipboard.writeText(link); } catch { /* clipboard may fail */ }
      toast.success("Convite criado", { description: "Link copiado para a área de transferência." });
      setInviteEmail("");
      const list = await Admin.listInvites();
      setInvites(list);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Não foi possível criar o convite.";
      toast.error(detail);
    } finally { setInviteBusy(false); }
  };

  const copyInviteLink = async (token) => {
    try {
      await navigator.clipboard.writeText(inviteLink(token));
      toast.success("Link copiado");
    } catch { toast.error("Não foi possível copiar"); }
  };

  const revokeInvite = async (token) => {
    if (!window.confirm("Revogar este convite?")) return;
    try {
      await Admin.revokeInvite(token);
      setInvites(prev => prev.filter(i => i.token !== token));
      toast.success("Convite revogado");
    } catch { toast.error("Não foi possível revogar"); }
  };

  const trialDaysLeft = (trial_until) => {
    if (!trial_until) return null;
    const ms = new Date(trial_until).getTime() - Date.now();
    if (isNaN(ms) || ms <= 0) return null;
    return Math.max(1, Math.ceil(ms / 86400000));
  };

  const setTrial = async (u, days) => {
    const iso = days ? new Date(Date.now() + days * 86400000).toISOString() : null;
    try {
      const updated = await Admin.setTrial(u.user_id, iso);
      setUsers(prev => prev.map(x => x.user_id === u.user_id ? updated : x));
      toast.success(days ? `Trial Pro · ${days} dias` : "Trial removido");
    } catch { toast.error("Não foi possível alterar o trial"); }
  };

  const upgradeTo = async (plan) => {
    if (plan === user.plan) { toast.message("Já estás neste plano"); return; }
    setCheckoutBusy(plan);
    try {
      const { url } = await Billing.checkout(plan);
      window.location.href = url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Não foi possível iniciar o pagamento.";
      toast.error(detail);
      setCheckoutBusy("");
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8" data-testid="settings-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold">{settingsTitle}</div>
          <h1 className="font-display text-4xl text-slate-50 mt-2 flex items-center gap-3">
            <SettingsIcon className="w-7 h-7 text-sky-400" strokeWidth={1.5} /> {settingsTitle}
          </h1>
          <p className="text-slate-400 text-sm mt-1">Perfil clínico, preferências de relatório, IA, avaliação{isAdmin ? " e gestão de acessos" : ""}.</p>
        </div>
        <div className="flex items-center gap-3">
          <Avatar className="h-10 w-10 ring-1 ring-slate-700">
            <AvatarImage src={user.picture} />
            <AvatarFallback className="bg-slate-800 text-slate-200 text-xs">{user.name?.slice(0,2).toUpperCase() || "US"}</AvatarFallback>
          </Avatar>
          <div className="text-right text-sm">
            <div className="text-slate-100">{user.name}</div>
            <div className="text-[11px] text-slate-500">{user.email} · Plano <span className="text-sky-300 capitalize">{user.plan}</span></div>
          </div>
          <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn" className="text-slate-400 hover:bg-slate-800">
            <LogOut className="w-4 h-4 mr-1" /> Sair
          </Button>
        </div>
      </header>

      <Tabs key={defaultTab} defaultValue={defaultTab} className="w-full">
        <TabsList className="bg-slate-900/60 border border-slate-800 rounded-3xl p-1 gap-1 w-full h-auto flex-wrap justify-start">
          <TabsTrigger value="clinic" data-testid="tab-clinic" className="rounded-full data-[state=active]:bg-sky-500 data-[state=active]:text-slate-950"><UserCog className="w-4 h-4 mr-2" />Perfil clínico</TabsTrigger>
          <TabsTrigger value="appearance" data-testid="tab-appearance" className="rounded-full data-[state=active]:bg-sky-500 data-[state=active]:text-slate-950"><Palette className="w-4 h-4 mr-2" />Aparência</TabsTrigger>
          <TabsTrigger value="report" data-testid="tab-report-prefs" className="rounded-full data-[state=active]:bg-sky-500 data-[state=active]:text-slate-950"><FileText className="w-4 h-4 mr-2" />Relatório</TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai-prefs" className="rounded-full data-[state=active]:bg-sky-500 data-[state=active]:text-slate-950"><Sparkles className="w-4 h-4 mr-2" />IA</TabsTrigger>
          <TabsTrigger value="assess" data-testid="tab-assess-prefs" className="rounded-full data-[state=active]:bg-sky-500 data-[state=active]:text-slate-950"><Shield className="w-4 h-4 mr-2" />Avaliação</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing" className="rounded-full data-[state=active]:bg-amber-400 data-[state=active]:text-slate-950"><CreditCard className="w-4 h-4 mr-2" />Plano</TabsTrigger>
          <TabsTrigger value="legal" data-testid="tab-legal" className="rounded-full data-[state=active]:bg-slate-200 data-[state=active]:text-slate-950"><ScrollText className="w-4 h-4 mr-2" />Termos e Condições</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations" className="rounded-full data-[state=active]:bg-cyan-500 data-[state=active]:text-slate-950"><Plug className="w-4 h-4 mr-2" />Integrações</TabsTrigger>
          {isAdmin && (
            <>
              <TabsTrigger value="branding" data-testid="tab-branding" className="rounded-full data-[state=active]:bg-pink-500 data-[state=active]:text-slate-950"><Palette className="w-4 h-4 mr-2" />Branding</TabsTrigger>
              <TabsTrigger value="content" data-testid="tab-content" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-950"><Type className="w-4 h-4 mr-2" />Conteúdo</TabsTrigger>
              <TabsTrigger value="aicontrol" data-testid="tab-ai-control" className="rounded-full data-[state=active]:bg-violet-500 data-[state=active]:text-slate-950"><Wand2 className="w-4 h-4 mr-2" />Controlo IA</TabsTrigger>
              <TabsTrigger value="admin" data-testid="tab-admin" className="rounded-full data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-950"><Users className="w-4 h-4 mr-2" />Terapeutas</TabsTrigger>
            </>
          )}
        </TabsList>

        {/* CLÍNICA */}
        <TabsContent value="clinic" className="mt-6">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="text-slate-300">Nome da clínica</Label>
              <Input data-testid="input-clinic-name" value={form.clinical_profile.clinic_name || ""} onChange={(e) => setForm({ ...form, clinical_profile: { ...form.clinical_profile, clinic_name: e.target.value } })} className="mt-1 bg-slate-900 border-slate-800 text-slate-50" />
            </div>
            <div>
              <Label className="text-slate-300">Nome do profissional</Label>
              <Input data-testid="input-prof-name" value={form.clinical_profile.professional_name || ""} onChange={(e) => setForm({ ...form, clinical_profile: { ...form.clinical_profile, professional_name: e.target.value } })} className="mt-1 bg-slate-900 border-slate-800 text-slate-50" />
            </div>
            <div>
              <Label className="text-slate-300">Contacto</Label>
              <Input data-testid="input-contact" value={form.clinical_profile.contact_info || ""} placeholder="+351 …" onChange={(e) => setForm({ ...form, clinical_profile: { ...form.clinical_profile, contact_info: e.target.value } })} className="mt-1 bg-slate-900 border-slate-800 text-slate-50" />
            </div>
            <div>
              <Label className="text-slate-300">Logo (≤2MB)</Label>
              <div className="flex gap-3 items-center mt-1">
                {form.clinical_profile.logo_url && <img src={form.clinical_profile.logo_url} alt="logo" className="w-12 h-12 rounded-md border border-slate-800 bg-white object-contain" />}
                <label className="cursor-pointer text-xs px-3 py-2 rounded-full border border-slate-700 text-slate-200 hover:bg-slate-800" data-testid="upload-logo">
                  <input type="file" accept="image/*" className="hidden" onChange={onLogoUpload} />
                  Carregar logo
                </label>
                {form.clinical_profile.logo_url && (
                  <button type="button" onClick={() => setForm({ ...form, clinical_profile: { ...form.clinical_profile, logo_url: "" } })} className="text-xs text-rose-400 hover:text-rose-300">Remover</button>
                )}
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-slate-300">Assinatura no relatório</Label>
              <Textarea data-testid="input-signature" rows={3} value={form.clinical_profile.signature || ""} placeholder="Ex.: Dr. Daniel Ribeiro · Osteopata OE-12345" onChange={(e) => setForm({ ...form, clinical_profile: { ...form.clinical_profile, signature: e.target.value } })} className="mt-1 bg-slate-900 border-slate-800 text-slate-50" />
            </div>
          </div>

          {/* Card de idioma da interface — preferência local, sem backend. */}
          <LanguagePreferenceCard />
        </TabsContent>

        {/* RELATÓRIO */}
        <TabsContent value="appearance" className="mt-6"><AppearancePanel /></TabsContent>

        <TabsContent value="report" className="mt-6">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 space-y-5">
            <ToggleRow label="Incluir observações pré-viscerais" testId="toggle-visceral"
                      checked={!!form.report_prefs.include_visceral}
                      onChange={(v) => setForm({ ...form, report_prefs: { ...form.report_prefs, include_visceral: v } })}/>
            <ToggleRow label="Incluir cadeias biomecânicas" testId="toggle-chains"
                      checked={!!form.report_prefs.include_chains}
                      onChange={(v) => setForm({ ...form, report_prefs: { ...form.report_prefs, include_chains: v } })}/>
            <ToggleRow label="Incluir plano de exercícios em casa" testId="toggle-home-plan"
                      checked={!!form.report_prefs.include_home_plan}
                      onChange={(v) => setForm({ ...form, report_prefs: { ...form.report_prefs, include_home_plan: v } })}/>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-3 border-t border-slate-800">
              <RadioGroup label="Linguagem" testId="radio-language"
                options={[["tecnica", "Técnica"], ["simplificada", "Simplificada"]]}
                value={form.report_prefs.language}
                onChange={(v) => setForm({ ...form, report_prefs: { ...form.report_prefs, language: v } })}/>
              <RadioGroup label="Layout" testId="radio-layout"
                options={[["completo", "Completo"], ["compacto", "Compacto"]]}
                value={form.report_prefs.layout}
                onChange={(v) => setForm({ ...form, report_prefs: { ...form.report_prefs, layout: v } })}/>
            </div>
          </div>
        </TabsContent>

        {/* IA */}
        <TabsContent value="ai" className="mt-6">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <RadioGroup label="Nível de detalhe" testId="radio-ai-detail"
              options={[["basico", "Básico"], ["avancado", "Avançado"]]}
              value={form.ai_prefs.detail}
              onChange={(v) => setForm({ ...form, ai_prefs: { ...form.ai_prefs, detail: v } })}/>
            <RadioGroup label="Estilo" testId="radio-ai-style"
              options={[["clinico", "Clínico"], ["pedagogico", "Pedagógico"]]}
              value={form.ai_prefs.style}
              onChange={(v) => setForm({ ...form, ai_prefs: { ...form.ai_prefs, style: v } })}/>
          </div>
        </TabsContent>

        {/* AVALIAÇÃO */}
        <TabsContent value="assess" className="mt-6">
          <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-6 space-y-5">
            <ToggleRow
              label="Género do paciente obrigatório"
              hint="Quando ativo, criar paciente sem género mostra erro."
              testId="toggle-gender-required"
              checked={!!form.assessment_prefs.gender_required}
              onChange={(v) => setForm({ ...form, assessment_prefs: { ...form.assessment_prefs, gender_required: v } })}/>
          </div>
        </TabsContent>

        {/* BILLING */}
        <TabsContent value="billing" className="mt-6">
          <div className="space-y-6" data-testid="billing-tab">
            {user.trial_active && (
              <div data-testid="self-trial-banner" className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4 text-sm text-violet-100 flex items-center gap-3">
                <Sparkles className="w-4 h-4 text-violet-300" />
                <div>
                  <span className="font-medium text-violet-200">Trial Pro ativo</span>
                  {(() => {
                    const left = Math.max(1, Math.ceil((new Date(user.trial_until).getTime() - Date.now()) / 86400000));
                    return <span className="text-violet-300/90"> · {left} dia(s) restantes</span>;
                  })()}
                  <div className="text-[11px] text-violet-200/70 mt-0.5">Acesso completo às funcionalidades Pro durante o período de teste.</div>
                </div>
              </div>
            )}
            <div className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-6 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold">Plano atual</div>
                <div className="font-display text-2xl text-slate-50 mt-1 capitalize" data-testid="current-plan">{user.plan}</div>
                <div className="text-xs text-slate-400 mt-1">Podes atualizar ou mudar de plano em qualquer momento.</div>
              </div>
              {user.role === "admin" && (
                <div className="text-[11px] text-slate-500 italic max-w-xs text-right">Como administrador, tens acesso total independentemente do plano.</div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {plans.map(p => {
                const isCurrent = p.id === user.plan;
                const accent = p.id === "premium" ? "violet" : p.id === "pro" ? "emerald" : "sky";
                const ring = {
                  sky: "border-sky-500/30 bg-sky-500/5",
                  emerald: "border-emerald-500/30 bg-emerald-500/5",
                  violet: "border-violet-500/30 bg-violet-500/5",
                }[accent];
                const textC = {
                  sky: "text-sky-300",
                  emerald: "text-emerald-300",
                  violet: "text-violet-300",
                }[accent];
                return (
                  <div key={p.id} data-testid={`plan-${p.id}`} className={`rounded-2xl border ${ring} p-6 relative`}>
                    {isCurrent && <span className="absolute top-3 right-3 text-[10px] px-2 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 uppercase tracking-wider">Atual</span>}
                    <div className={`text-xs uppercase tracking-[0.2em] font-semibold ${textC}`}>{p.label}</div>
                    <div className="mt-2 flex items-baseline gap-1">
                      <span className="font-display text-4xl text-slate-50">€{p.amount}</span>
                      <span className="text-xs text-slate-500">/mês</span>
                    </div>
                    <ul className="mt-5 space-y-2 text-sm text-slate-300 min-h-[7rem]">
                      <PlanFeature label="Avaliações ilimitadas" ok />
                      <PlanFeature label="Biblioteca Funcional editável" ok={p.features.library_edit} />
                      <PlanFeature label="Comparação entre avaliações" ok={p.features.comparison} />
                      <PlanFeature label="Observações pré-viscerais" ok={p.features.visceral} />
                      <PlanFeature label="PDF com branding da clínica" ok={p.features.pdf_whitelabel} />
                    </ul>
                    <Button
                      data-testid={`upgrade-${p.id}-btn`}
                      disabled={isCurrent || checkoutBusy === p.id}
                      onClick={() => upgradeTo(p.id)}
                      className={`w-full mt-5 rounded-full font-semibold ${isCurrent
                        ? "bg-slate-800 text-slate-400 hover:bg-slate-800 cursor-default"
                        : "bg-amber-400 hover:bg-amber-300 text-slate-950"}`}
                    >
                      {isCurrent ? "Plano atual" : checkoutBusy === p.id ? "A redirecionar…" : `Escolher ${p.label}`}
                    </Button>
                  </div>
                );
              })}
            </div>

            <div className="text-[11px] text-slate-500 text-center">
              Pagamento processado por Stripe em ambiente seguro. Podes cancelar a qualquer momento.
            </div>
          </div>
        </TabsContent>

        {/* LEGAL */}
        <TabsContent value="legal" className="mt-6">
          <div className="space-y-6" data-testid="legal-tab">
            <LegalSection
              testId="legal-clinical-use"
              eyebrow="Uso Clínico"
              title="Versão curta — para consulta rápida"
              body={CLINICAL_USE_SHORT}
              tone="sky"
            />
            <LegalSection
              testId="legal-clinical-notice"
              eyebrow="Aviso Clínico"
              title="Versão intermédia — contexto e responsabilidade"
              body={CLINICAL_NOTICE_MEDIUM}
              tone="amber"
            />
            <LegalSection
              testId="legal-full-notice"
              eyebrow="Aviso Legal completo"
              title="Versão integral — RGPD, MDR e limitações"
              body={LEGAL_NOTICE_FULL}
              tone="slate"
            />
          </div>
        </TabsContent>

        {isAdmin && (
          <>
            <TabsContent value="branding" className="mt-6"><BrandingPanel /></TabsContent>
            <TabsContent value="content" className="mt-6"><ContentPanel /></TabsContent>
            <TabsContent value="aicontrol" className="mt-6"><AiControlPanel /></TabsContent>
          </>
        )}

        {/* Integrações — disponível para todos os terapeutas */}
        <TabsContent value="integrations" className="mt-6"><IntegrationsPanel /></TabsContent>

        {/* ADMIN */}
        {isAdmin && (
          <TabsContent value="admin" className="mt-6 space-y-6">
            {user?.mio_sign_enabled && <SignRulesPanel />}
            {/* Convidar terapeuta */}
            <div className="rounded-2xl border border-sky-500/20 bg-slate-900/50 p-6" data-testid="invite-section">
              <div className="text-xs uppercase tracking-[0.2em] text-sky-400 font-semibold mb-3 flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" /> Convidar terapeuta
              </div>
              <p className="text-[12px] text-slate-400 mb-3">Cria um link de convite para um email. Ao primeiro login Google com esse email, a conta é criada como terapeuta.</p>
              <div className="flex gap-2 flex-wrap">
                <Input
                  data-testid="invite-email-input"
                  type="email"
                  placeholder="terapeuta@clinica.pt"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") sendInvite(); }}
                  className="bg-slate-950 border-slate-800 text-slate-50 flex-1 min-w-[14rem]"
                />
                <Button
                  data-testid="invite-send-btn"
                  onClick={sendInvite}
                  disabled={inviteBusy}
                  className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full px-5"
                >
                  <Mail className="w-4 h-4 mr-2" /> {inviteBusy ? "A criar…" : "Convidar"}
                </Button>
              </div>

              {invites.length > 0 && (
                <div className="mt-5 space-y-2" data-testid="invites-list">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Convites</div>
                  {invites.map(inv => (
                    <div key={inv.token} data-testid={`invite-row-${inv.token}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-950/40 p-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-100 truncate">{inv.email}</div>
                        <div className="text-[11px] text-slate-500">
                          {inv.used ? (
                            <span className="text-emerald-300">Aceite{inv.used_at ? ` · ${new Date(inv.used_at).toLocaleDateString("pt-PT")}` : ""}</span>
                          ) : (
                            <span>Pendente · criado em {new Date(inv.created_at).toLocaleDateString("pt-PT")}</span>
                          )}
                        </div>
                      </div>
                      {!inv.used && (
                        <>
                          <button
                            data-testid={`copy-invite-${inv.token}`}
                            onClick={() => copyInviteLink(inv.token)}
                            className="p-2 rounded-full text-slate-400 hover:text-sky-300 hover:bg-slate-800"
                            title="Copiar link"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                          <a
                            data-testid={`mailto-invite-${inv.token}`}
                            href={`mailto:${encodeURIComponent(inv.email)}?subject=${encodeURIComponent("Convite — MIO")}&body=${encodeURIComponent(`Olá,\n\nFoste convidado(a) para a plataforma MIO.\nAcede aqui: ${inviteLink(inv.token)}\n`)}`}
                            className="p-2 rounded-full text-slate-400 hover:text-emerald-300 hover:bg-slate-800"
                            title="Abrir email"
                          >
                            <Mail className="w-4 h-4" />
                          </a>
                          <button
                            data-testid={`revoke-invite-${inv.token}`}
                            onClick={() => revokeInvite(inv.token)}
                            className="p-2 rounded-full text-slate-400 hover:text-rose-300 hover:bg-slate-800"
                            title="Revogar"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Lista de terapeutas */}
            <div className="rounded-2xl border border-emerald-500/20 bg-slate-900/50 p-6">
              <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 font-semibold mb-3">Gestão de acessos</div>
              {users.length === 0 ? (
                <div className="text-sm text-slate-500">Ainda sem outros terapeutas registados.</div>
              ) : (
                <div className="space-y-2">
                  {users.map(u => {
                    const tDays = trialDaysLeft(u.trial_until);
                    return (
                    <div key={u.user_id} data-testid={`therapist-row-${u.user_id}`} className="flex items-center justify-between gap-3 rounded-xl border border-slate-800 bg-slate-950/40 p-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={u.picture}/>
                          <AvatarFallback className="bg-slate-800 text-slate-300 text-xs">{u.name?.slice(0,2).toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="text-slate-100 text-sm flex items-center gap-2">
                            {u.name}
                            {tDays && (
                              <span data-testid={`trial-badge-${u.user_id}`} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-300 uppercase tracking-wider">
                                <Sparkles className="w-2.5 h-2.5" /> Trial Pro · {tDays}d
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] text-slate-500">{u.email} · <span className="capitalize">{u.role}</span> · Plano <span className="text-sky-300 capitalize">{u.plan}</span></div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {u.role !== "admin" && (
                          <>
                            <button
                              data-testid={`grant-trial-${u.user_id}`}
                              onClick={() => setTrial(u, 14)}
                              title="Conceder/estender Trial Pro 14 dias"
                              className="text-[11px] px-2.5 py-1 rounded-full border border-violet-500/30 bg-violet-500/5 text-violet-200 hover:bg-violet-500/10"
                            >
                              {tDays ? "+14d" : "Trial 14d"}
                            </button>
                            {tDays && (
                              <button
                                data-testid={`clear-trial-${u.user_id}`}
                                onClick={() => setTrial(u, 0)}
                                title="Terminar trial agora"
                                className="text-[11px] px-2.5 py-1 rounded-full border border-slate-700 text-slate-400 hover:bg-slate-800"
                              >
                                Limpar
                              </button>
                            )}
                          </>
                        )}
                        <span className={`text-[11px] px-2 py-0.5 rounded-full ${u.is_active ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-rose-500/10 text-rose-300 border border-rose-500/30"}`}>
                          {u.is_active ? "Ativo" : "Desativado"}
                        </span>
                        <Switch
                          data-testid={`toggle-therapist-${u.user_id}`}
                          checked={!!u.is_active}
                          onCheckedChange={() => toggleTherapist(u)}
                          disabled={u.user_id === user.user_id}
                        />
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      <div className={`flex justify-end ${defaultTab === "appearance" ? "hidden" : ""}`}>
        <Button data-testid="save-settings-btn" onClick={save} disabled={busy} className="bg-sky-500 hover:bg-sky-400 text-slate-950 rounded-full px-6 glow-sky">
          <Save className="w-4 h-4 mr-2" /> {busy ? "A guardar…" : "Guardar alterações"}
        </Button>
      </div>
    </div>
  );
}

function ToggleRow({ label, hint, checked, onChange, testId }) {  return (
    <div className="flex items-center justify-between gap-3">
      <div>
        <div className="text-sm text-slate-100">{label}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
      <Switch data-testid={testId} checked={checked} onCheckedChange={onChange} />
    </div>
  );
}

function PlanFeature({ label, ok }) {
  return (
    <li className="flex items-start gap-2">
      <span className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full ${ok ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40" : "bg-slate-800/60 text-slate-600 border border-slate-700"}`}>
        <Check className="w-2.5 h-2.5" strokeWidth={3} />
      </span>
      <span className={ok ? "text-slate-200" : "text-slate-500 line-through decoration-slate-700"}>{label}</span>
    </li>
  );
}

function LegalSection({ eyebrow, title, body, tone, testId }) {
  const tones = {
    sky:    "border-sky-500/20 bg-sky-500/5 text-sky-300",
    amber:  "border-amber-500/20 bg-amber-500/5 text-amber-300",
    slate:  "border-slate-700 bg-slate-900/50 text-slate-300",
  };
  const t = tones[tone] || tones.slate;
  const eyebrowColor = tone === "sky" ? "text-sky-300" : tone === "amber" ? "text-amber-300" : "text-slate-400";
  return (
    <section data-testid={testId} className={`rounded-2xl border p-6 ${t.split(" ").slice(0,2).join(" ")}`}>
      <div className={`text-[11px] uppercase tracking-[0.2em] font-semibold ${eyebrowColor}`}>{eyebrow}</div>
      <div className="font-display text-xl text-slate-50 mt-1">{title}</div>
      <div className="mt-3 text-sm text-slate-300 leading-relaxed whitespace-pre-line">{body}</div>
    </section>
  );
}

function RadioGroup({ label, options, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-slate-300 text-sm">{label}</Label>
      <div className="mt-2 inline-flex p-1 rounded-full bg-slate-950/60 border border-slate-800" data-testid={testId}>
        {options.map(([v, lbl]) => (
          <button
            key={v}
            type="button"
            data-testid={`${testId}-${v}`}
            onClick={() => onChange(v)}
            className={`px-4 py-1.5 text-xs rounded-full transition ${value === v ? "bg-sky-500 text-slate-950 font-semibold" : "text-slate-400 hover:text-slate-200"}`}
          >
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}


function LanguagePreferenceCard() {
  const tx = useT();
  const [current, setCurrent] = useState(getLang());

  const apply = (code) => {
    if (setLang(code)) {
      setCurrent(code);
      toast.success(tx("settings.language.save"));
    }
  };

  return (
    <div data-testid="language-pref-card" className="mt-6 rounded-2xl border border-slate-800 bg-slate-900/50 p-6">
      <div className="flex items-center gap-2">
        <Globe className="w-4 h-4 text-sky-300" strokeWidth={1.5} />
        <div className="text-xs uppercase tracking-[0.2em] text-sky-300 font-semibold">
          {tx("settings.language.title")}
        </div>
      </div>
      <p className="text-sm text-slate-400 mt-2">{tx("settings.language.body")}</p>

      <div className="mt-4 flex flex-wrap gap-2" role="radiogroup" aria-label="Language">
        {LANGS.map((l) => {
          const on = current === l.code;
          return (
            <button
              key={l.code}
              type="button"
              role="radio"
              aria-checked={on}
              data-testid={`lang-option-${l.code}`}
              onClick={() => apply(l.code)}
              className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full border text-sm transition ${
                on
                  ? "bg-sky-500/15 border-sky-500/50 text-sky-100"
                  : "bg-slate-950/40 border-slate-800 text-slate-400 hover:text-slate-200 hover:border-slate-700"
              }`}
            >
              <span className="text-base leading-none">{l.flag}</span>
              <span>{l.label}</span>
              {on && <Check className="w-3.5 h-3.5 text-sky-300" />}
            </button>
          );
        })}
      </div>

      <div className="text-[11px] text-slate-500 italic mt-3">
        {tx("settings.language.note")}
      </div>
    </div>
  );
}
